import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, startOfDay, endOfDay } from "date-fns";
import { staffSummaryForDay } from "@/lib/staffConflictSummary";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { X } from "lucide-react";
import { getAuthToken } from "@/lib/authToken";
import { getApiUrl } from "@/lib/queryClient";
import type { StaffAvailability } from "@/shared/types";

interface StaffMember {
  id: string;
  firstName: string;
  lastName: string;
  workdayStart: string;
  workdayEnd: string;
  staffType?: string;
}

interface CalendarEvent {
  id: string;
  assignedTo?: string;
  assignedStaff?: { id: string }[];
  scheduledStartDate?: Date;
  scheduledEndDate?: Date;
  startTime: Date;
  endTime: Date;
  date: Date;
  source?: string;
  highestConflictSeverity?: string;
}

interface StaffAssignmentPanelProps {
  open: boolean;
  onClose: () => void;
  staff: StaffMember[];
  selectedStaffIds?: string[];
  onSave: (staffIds: string[]) => void;
  date: Date;
  events: CalendarEvent[];
}

export function StaffAssignmentPanel({ 
  open, 
  onClose, 
  staff, 
  selectedStaffIds = [], 
  onSave, 
  date,
  events 
}: StaffAssignmentPanelProps) {
  const [chosen, setChosen] = useState<string[]>([]);

  useEffect(() => {
    setChosen(selectedStaffIds || []);
  }, [selectedStaffIds]);

  // Fetch availability for all staff for the selected date
  const { data: availability = [] } = useQuery<StaffAvailability[]>({
    queryKey: ["/staff-availability", format(date, "yyyy-MM-dd")],
    queryFn: async () => {
      const startDate = startOfDay(date);
      const endDate = endOfDay(date);
      const token = getAuthToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
      const url = getApiUrl(`/staff-availability?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`);
      const response = await fetch(url, { headers });
      if (!response.ok) return [];
      return response.json();
    },
    enabled: open,
  });

  // Check if a staff member is unavailable on the selected date
  const isStaffUnavailable = (staffId: string): boolean => {
    const dateKey = format(date, "yyyy-MM-dd");
    return availability.some(a => {
      const availDate = a.date ? format(new Date(a.date), "yyyy-MM-dd") : null;
      return a.staffId === staffId && 
             availDate === dateKey && 
             a.status === "unavailable";
    });
  };

  // Get availability notes for a staff member
  const getAvailabilityNotes = (staffId: string): string | null => {
    const dateKey = format(date, "yyyy-MM-dd");
    const avail = availability.find(a => {
      const availDate = a.date ? format(new Date(a.date), "yyyy-MM-dd") : null;
      return a.staffId === staffId && 
             availDate === dateKey && 
             a.status === "unavailable";
    });
    return avail?.notes || null;
  };

  if (!open) return null;

  const sortedStaff = useMemo(
    () =>
      [...staff].sort((a, b) =>
        `${a.firstName} ${a.lastName}`.localeCompare(
          `${b.firstName} ${b.lastName}`,
          undefined,
          { sensitivity: "base" }
        )
      ),
    [staff]
  );

  function toggle(id: string) {
    if (chosen.includes(id)) {
      setChosen(chosen.filter(x => x !== id));
    } else {
      setChosen([...chosen, id]);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Dark overlay */}
      <div 
        className="flex-1 bg-black/30" 
        onClick={onClose}
        data-testid="overlay-staff-assignment"
      />

      {/* Slide-in panel */}
      <div 
        className="w-80 bg-white shadow-xl p-5 border-l border-[var(--wd-gray-200)] animate-slide-in-from-right"
        data-testid="panel-staff-assignment"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold">Assign Staff</h2>
          <Button 
            size="icon" 
            variant="ghost"
            onClick={onClose}
            data-testid="button-close-staff-panel"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        {/* Staff list */}
        <div className="flex flex-col gap-3 max-h-[calc(100vh-200px)] overflow-y-auto">
          {sortedStaff.map(s => {
            const staffName = `${s.firstName} ${s.lastName}`;
            const typeLabel = s.staffType === 'contractor' ? ' (Contractor)' : ' (Employee)';
            
            // Get events for this staff member
            const staffEvents = events.filter(event => 
              event.assignedTo === s.id ||
              event.assignedStaff?.some(staff => staff.id === s.id)
            );
            
            const summary = staffSummaryForDay(s.id, staffEvents, date);
            const isChosen = chosen.includes(s.id);
            const unavailable = isStaffUnavailable(s.id);
            const availabilityNotes = getAvailabilityNotes(s.id);
            
            return (
              <div 
                key={s.id}
                className={`border border-[var(--wd-gray-200)] p-3 rounded hover:bg-[var(--wd-gray-50)] cursor-pointer flex justify-between items-center gap-3 ${
                  isChosen ? 'bg-[var(--wd-blue-50)] border-[var(--wd-blue-500)]' : ''
                } ${unavailable ? 'opacity-75' : ''}`}
                onClick={() => toggle(s.id)}
                data-testid={`staff-option-${s.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{staffName}{typeLabel}</div>

                  {/* Availability summary badges - Outlook color system */}
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {/* Show unavailability FIRST - most important */}
                    {unavailable && (
                      <Badge 
                        variant="destructive" 
                        className="text-xs"
                        title={availabilityNotes || "Staff member is unavailable on this date"}
                      >
                        🚫 Unavailable
                        {availabilityNotes && " - " + availabilityNotes}
                      </Badge>
                    )}
                    {!unavailable && summary.criticalConflicts > 0 && (
                      <div 
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: '#FEE2E2',
                          color: '#DC2626',
                          border: '1px solid #FECACA',
                        }}
                      >
                        ⛔ {summary.criticalConflicts} Critical
                      </div>
                    )}
                    {!unavailable && summary.criticalConflicts === 0 && summary.highConflicts > 0 && (
                      <div 
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: '#FEF3C7',
                          color: '#D97706',
                          border: '1px solid #FDE68A',
                        }}
                      >
                        ⚠ {summary.highConflicts} Conflicts
                      </div>
                    )}
                    {!unavailable && summary.busyNow && (
                      <div className="text-xs px-2 py-0.5 bg-[var(--wd-blue-50)] text-[var(--wd-blue-500)] border border-[var(--wd-blue-100)] rounded">
                        Busy until {summary.busyUntil}
                      </div>
                    )}
                    {!unavailable && !summary.busyNow && summary.criticalConflicts === 0 && summary.highConflicts === 0 && (
                      <div 
                        className="text-xs px-2 py-0.5 rounded"
                        style={{
                          backgroundColor: 'var(--wd-gray-100)',
                          color: 'var(--wd-gray-700)',
                          border: '1px solid var(--wd-gray-200)',
                        }}
                      >
                        ✓ Available
                      </div>
                    )}
                  </div>
                </div>

                <Checkbox 
                  checked={isChosen}
                  onCheckedChange={() => toggle(s.id)}
                  onClick={(e) => e.stopPropagation()}
                  data-testid={`checkbox-staff-${s.id}`}
                />
              </div>
            );
          })}
        </div>

        {/* Action buttons */}
        <div className="flex gap-2 mt-6">
          <Button
            variant="outline"
            className="flex-1"
            onClick={onClose}
            data-testid="button-cancel-staff"
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={() => {
              onSave(chosen);
              onClose();
            }}
            data-testid="button-save-staff"
          >
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
}
